import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { useAppDispatch } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import { getUrlParams, noop } from 'helpers/utils';
import { useReadyGate } from 'hooks/useReadyGate';
import {
  useCreateCartMutation,
  useGetCurrentCartMutation,
  useLazyGetCartQuery,
  useUpdateCartMutation,
} from 'services/savedCartApi';
import { shoppingCartApi } from 'services/shoppingCartApi';
import { CartSaver } from './cartSaver';
import {
  FailReason,
  CartRestoreMode,
  RestoreFailure,
  CartRestorePhase,
  errorStatus,
  isTransientFailure,
  loadSavedCart,
} from './loadSavedCart';

const SAVED_DONOR_CARTS_GATE = 'saved_donor_carts';
const RESTORE_DEADLINE_MS = 3000;
// How long Donate waits for a save in flight before navigating.
const DONATE_FLUSH_CAP_MS = 1000;

/**
 * - waiting: gate or Auth0 not known yet; behaves as gate-off.
 * - restoring: adds disabled until the saved cart loads.
 * - active: saving on.
 * - off: gate off, gate on after the donor already picked, or restore failed. Final.
 */
type SavedCartPhase = 'waiting' | 'restoring' | 'active' | 'off';

export interface UseSavedCartArgs {
  clientId: number;
  donorCartParam?: string;
  selectedRecipients: CandidateType[];
  setSelectedRecipients: Dispatch<SetStateAction<CandidateType[]>>;
}

export function useSavedCart({
  clientId,
  donorCartParam,
  selectedRecipients,
  setSelectedRecipients,
}: UseSavedCartArgs) {
  const dispatch = useAppDispatch();
  const gate = useReadyGate(SAVED_DONOR_CARTS_GATE);
  const { isLoading: isAuth0Loading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [getCurrentCart] = useGetCurrentCartMutation();
  const [getCart] = useLazyGetCartQuery();
  const [createCart] = useCreateCartMutation();
  const [updateCart] = useUpdateCartMutation();

  const [savedCartPhase, setSavedCartPhase] = useState<SavedCartPhase>('waiting');
  // Set when the server rejects the JWT: later cart requests go by guest token only.
  const jwtRejectedRef = useRef(false);
  // Created when restore succeeds; not in an effect, so StrictMode's re-run can't replace it.
  const saverRef = useRef<CartSaver>();

  const selectedIds = useMemo(
    () => selectedRecipients.map(r => r.recipient_id),
    [selectedRecipients]
  );

  useEffect(() => {
    if (savedCartPhase !== 'waiting' || gate === 'pending' || isAuth0Loading) return;
    if (gate === 'off' || selectedRecipients.length > 0) {
      // Restoring now would overwrite the donor's picks, and saving would replace their saved cart.
      setSavedCartPhase('off');
      return;
    }
    setSavedCartPhase('restoring');
  }, [savedCartPhase, gate, isAuth0Loading, selectedRecipients.length]);

  useEffect(() => {
    if (savedCartPhase !== 'restoring') return;

    let settled = false;
    const restoreStartedAt = Date.now();
    let cartRestorePhase: CartRestorePhase = 'cart';
    const cartRestoreMode: CartRestoreMode = isAuthenticated
      ? 'logged_in'
      : donorCartParam
        ? 'guest_link'
        : 'guest';

    const getJwt = async () => {
      if (!isAuthenticated || jwtRejectedRef.current) return undefined;
      return (await getAccessTokenSilently().catch(noop)) || undefined;
    };

    const startSaving = (seed: { cartId?: string; ids: string[] }) => {
      saverRef.current = new CartSaver(
        {
          clientId,
          refCode: getUrlParams().urlRef,
          createCart,
          updateCart,
          getJwt,
          onSaved: (cart, ids) => {
            trackEvent('Donor Cart Updated', {
              cart_id: cart.id,
              client_id: clientId,
              page_query_string: cart.page_query_string,
              recipient_ids: ids,
              recipient_count: ids.length,
            });
          },
        },
        seed
      );
      setSavedCartPhase('active');
    };

    const settle = () => {
      settled = true;
      clearTimeout(deadline);
    };

    const fail = (reason: FailReason, status?: unknown, isTransient = false) => {
      if (settled) return;
      settle();
      setSavedCartPhase('off');
      if (cartRestoreMode === 'guest_link' && (reason === 'not_found' || reason === 'closed')) {
        trackEvent('Donor Cart Restore Failed', {
          cart_id: donorCartParam as string,
          client_id: clientId,
          reason,
        });
      }
      // Expected outcomes, or one that repeats on every view for a client with no cart page.
      const isExpected = ['no_cart_page', 'not_found', 'closed'].includes(reason);
      const level = isExpected ? 'info' : reason === 'deadline' || isTransient ? 'warn' : 'error';
      datadogLogs.logger[level]('Saved cart restore failed; saving is off for this page view', {
        client_id: clientId,
        cart_restore_mode: cartRestoreMode,
        cart_restore_phase: cartRestorePhase,
        reason,
        http_status: status,
        elapsed_ms: Date.now() - restoreStartedAt,
      });
    };

    const deadline = setTimeout(() => fail('deadline'), RESTORE_DEADLINE_MS);

    loadSavedCart({
      cartRestoreMode,
      clientId,
      donorCartParam,
      getJwt,
      onJwtRejected: () => {
        jwtRejectedRef.current = true;
      },
      getCurrentCart,
      getCart,
      // Shares SearchScreen's cached (or in-flight) config request.
      getClientCartConfig: () =>
        dispatch(
          shoppingCartApi.endpoints.getShoppingCartSearchConfig.initiate(
            { client_id: clientId },
            { subscribe: false }
          )
        ).unwrap(),
      isCancelled: () => settled,
      setCartRestorePhase: phase => {
        cartRestorePhase = phase;
      },
    })
      .then(loaded => {
        if (!loaded || settled) return;
        settle();
        if (!('recipients' in loaded)) {
          // Keep an empty cart's id so later adds PATCH it instead of POSTing a new one.
          startSaving({ cartId: loaded.cart?.id, ids: [] });
          return;
        }
        const { cart, recipients, config } = loaded;
        const restoredIds = recipients.map(r => r.recipient_id);
        setSelectedRecipients(recipients.map((r, index) => ({ ...r, index, isSelected: true })));
        // Seeded with what was restored, so it isn't saved back; dropped ids leave with the next save.
        startSaving({ cartId: cart.id, ids: restoredIds });
        trackEvent('Donor Cart Restored', {
          cart_id: cart.id,
          client_id: clientId,
          page_query_string: config.query_string,
          recipient_ids: restoredIds,
          recipient_count: recipients.length,
          dropped_recipient_count: new Set(cart.recipient_ids).size - recipients.length,
          restore_duration_ms: Date.now() - restoreStartedAt,
        });
      })
      .catch(error => {
        if (error instanceof RestoreFailure) fail(error.reason, error.status, error.isTransient);
        else fail('error', errorStatus(error), isTransientFailure(error));
      });

    return () => {
      // Unmounted mid-restore: drop whatever resolves later, quietly.
      if (!settled) settle();
    };
    // Runs once on entering 'restoring'; the inputs it reads are fixed for the page view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCartPhase]);

  useEffect(() => {
    if (savedCartPhase === 'active') saverRef.current?.update(selectedIds);
  }, [savedCartPhase, selectedIds]);

  // Unmount is an in-app navigation, so the page lives on to finish the save.
  useEffect(() => () => saverRef.current?.drainNow(), []);

  const flushBeforeNavigation = useCallback(
    (): Promise<string | undefined> =>
      saverRef.current?.flushBeforeUnload(DONATE_FLUSH_CAP_MS) ?? Promise.resolve(undefined),
    []
  );

  return {
    isRestoring: savedCartPhase === 'restoring',
    isSavingActive: savedCartPhase === 'active',
    flushBeforeNavigation,
  };
}
